import React, { useEffect } from 'react';
import T from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Button } from '@mui/material';
import { StyledContent, StyledHandlers, StyledNewDialog } from '../elements';
import { updateMachineAutomationSystemConfig } from 'redux/machines/actions';
import { IrisV3InsideConfigurationSchema } from 'attrs/formValidation';
import { Formik } from 'formik';
import { TextField } from 'web-components-ts';
import { useSelector, useDispatch } from 'react-redux';
import { getAutomationSystemConfig } from 'redux/machines/selectors';
import { loadMachineAutomationSystemConfig } from '../../../../redux/machines/actions';
import { SUCCESS } from 'attrs/status';

interface EditAutomationSystemConfigurationIRISV3InsideProps {
  open: boolean;
  handleClose: () => void;
  machineId: string;
}

const EditAutomationSystemConfigurationIRISV3Inside = ({
  open,
  handleClose,
  machineId
}: EditAutomationSystemConfigurationIRISV3InsideProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data: automationSystemConfig, loadingState: automationSystemConfigLoadingState } =
    useSelector(getAutomationSystemConfig);

  useEffect(() => {
    dispatch(loadMachineAutomationSystemConfig(machineId));
  }, [dispatch, machineId]);

  const handleUpdateAutomationSystemConfiguration = (values: {
    address: string;
    rack: number;
    port: number;
    slot: number;
  }) => {
    const iris_v3_inside_s7_payload = {
      iris_v3_inside_s7: {
        address: values.address,
        port: Number(values.port),
        rack: Number(values.rack),
        slot: Number(values.slot)
      }
    };
    dispatch(updateMachineAutomationSystemConfig(machineId, iris_v3_inside_s7_payload));
    handleClose();
  };

  if (automationSystemConfigLoadingState.status !== SUCCESS) return null;

  return (
    <Formik
      initialValues={{
        address: automationSystemConfig?.iris_v3_inside_s7?.address,
        rack: automationSystemConfig?.iris_v3_inside_s7?.rack,
        port: automationSystemConfig?.iris_v3_inside_s7?.port,
        slot: automationSystemConfig?.iris_v3_inside_s7?.slot
      }}
      validationSchema={IrisV3InsideConfigurationSchema}
      onSubmit={handleUpdateAutomationSystemConfiguration}
    >
      {({ values, handleChange, handleBlur, handleSubmit, errors }) => {
        const actionBar = (
          <StyledHandlers>
            <Button color="secondary" onClick={handleClose}>
              {t('form.cancel')}
            </Button>
            <Button
              variant="contained"
              style={{ padding: '1rem 2.5rem' }}
              onClick={() => handleSubmit()}
              disabled={Boolean(errors.address || errors.slot)}
            >
              {t('form.save')}
            </Button>
          </StyledHandlers>
        );

        return (
          <StyledNewDialog
            open={open}
            handleClose={handleClose}
            title={t('machines.detail.automation_system.configuration.edit_automation_system_configuration')}
            subTitle={t('machines.detail.automation_system.configuration.subtitle')}
            maxWidth="1062px"
            displayActionBar="true"
            actionBar={actionBar}
            content={
              <StyledContent>
                <form>
                  <Grid container spacing={2} columns={12}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label={t('machines.detail.automation_system.configuration.ip_address')}
                        id="address"
                        margin="normal"
                        name="address"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address}
                        required
                        type="text"
                        error={Boolean(errors.address)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label={t('machines.detail.automation_system.configuration.port')}
                        id="port"
                        margin="normal"
                        name="port"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.port}
                        required
                        type="text"
                        error={Boolean(errors.port)}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} columns={12}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label={t('machines.detail.automation_system.configuration.rack')}
                        id="rack"
                        margin="normal"
                        name="rack"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.rack}
                        required
                        type="number"
                        error={Boolean(errors.rack)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label={t('machines.detail.automation_system.configuration.slot')}
                        id="slot"
                        margin="normal"
                        name="slot"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.slot}
                        required
                        type="number"
                        error={Boolean(errors.slot)}
                      />
                    </Grid>
                  </Grid>
                </form>
              </StyledContent>
            }
          />
        );
      }}
    </Formik>
  );
};

EditAutomationSystemConfigurationIRISV3Inside.propTypes = {
  open: T.bool.isRequired,
  handleClose: T.func.isRequired,
  machineId: T.string.isRequired,
  automationSystem: T.shape({
    plc_type: T.string
  }).isRequired
};

export default EditAutomationSystemConfigurationIRISV3Inside;
